<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center min-h-screen">
      <div class="text-center">
        <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Laddar evenemang...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="min-h-screen flex items-center justify-center">
      <!-- Show LoginRequired component for auth errors -->
      <LoginRequired 
        v-if="error === 'Du behöver logga in för att se evenemang'"
        message="Du behöver logga in för att se evenemang"
      />
      
      <!-- Show regular error for other errors -->
      <div v-else class="text-center max-w-md mx-auto">
        <div class="text-red-500 mb-6">
          <svg class="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
          </svg>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-2">Ett fel uppstod</h3>
        <p class="text-gray-600 mb-6">{{ error }}</p>
        <button 
          @click="fetchEvents"
          class="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
        >
          Försök igen
        </button>
      </div>
    </div>

    <!-- Content -->
    <div v-else class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">


      <!-- My Events Section (only for organizers/admin) -->
      <div v-if="currentUser && (currentUser.isOrganizer || currentUser.role === 'organizer' || currentUser.role === 'admin')" class="mb-12">
        <div class="flex items-center justify-between mb-8">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <h2 class="text-2xl font-bold text-gray-900">Mina Evenemang</h2>
              <p class="text-gray-600">Evenemang som du har skapat</p>
            </div>
          </div>
          <router-link 
            v-if="currentUser && (currentUser.isOrganizer || currentUser.role === 'organizer' || currentUser.role === 'admin')"
            to="/events/create"
            class="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Skapa Event
          </router-link>
        </div>
        
        <!-- My Events Grid -->
        <div v-if="myEvents.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div 
            v-for="event in myEvents" 
            :key="event._id"
            class="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
          >
            <!-- Event Image -->
            <div class="h-48 overflow-hidden relative">
              <img 
                v-if="event.imageUrl && event.imageUrl.startsWith('data:')" 
                :src="event.imageUrl" 
                :alt="event.title"
                class="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
              <div v-else class="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <svg class="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                </svg>
              </div>
              <!-- Organizer Badge -->
              <div class="absolute top-3 left-3">
                <span class="bg-indigo-600 text-white px-2 py-1 rounded-full text-xs font-medium">Mitt Event</span>
              </div>
            </div>
            
            <!-- Event Content -->
            <div class="p-6">
              <h3 class="text-xl font-semibold text-gray-900 mb-3 line-clamp-1">{{ event.title }}</h3>
              <p class="text-gray-600 mb-4 line-clamp-2">{{ event.description }}</p>
              
              <!-- Event Details -->
              <div class="space-y-3 text-sm text-gray-500 mb-6">
                <div class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <span>{{ formatDate(event.date) }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                  <span class="line-clamp-1">{{ event.location }}</span>
                </div>
              </div>
              
              <!-- Action Buttons -->
              <div class="flex flex-wrap gap-2">
                <router-link 
                  :to="`/events/${event._id}`"
                  class="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors text-center"
                >
                  Se detaljer
                </router-link>
                <router-link 
                  :to="`/events/${event._id}/edit`"
                  class="flex-1 bg-yellow-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-yellow-600 transition-colors text-center"
                >
                  Redigera
                </router-link>
                <router-link 
                  :to="`/events/${event._id}/dashboard`"
                  class="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors text-center"
                >
                  Dashboard
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State for My Events -->
        <div v-else class="text-center py-16 bg-white rounded-xl shadow-lg border border-gray-100">
          <div class="w-24 h-24 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg class="w-12 h-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">Du har inte skapat något event än</h3>
          <p class="text-gray-600 mb-6">Börja med att skapa ditt första evenemang!</p>
          <router-link 
            v-if="currentUser && (currentUser.isOrganizer || currentUser.role === 'organizer' || currentUser.role === 'admin')"
            to="/events/create"
            class="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Skapa ditt första evenemang
          </router-link>
          <div v-else class="text-sm text-gray-500">Du saknar rättigheter att skapa event.</div>
        </div>
      </div>

      <!-- All Events Section (hidden for organizers/admin; hidden for buyers per request) -->
      <div v-if="false" class="mb-12">
        <div class="flex items-center justify-between mb-8">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                </svg>
              </div>
            </div>
            <div class="ml-4">
              <h2 class="text-2xl font-bold text-gray-900">Alla Evenemang</h2>
              <p class="text-gray-600">Upptäck evenemang från andra arrangörer</p>
            </div>
          </div>
        </div>
        
        <!-- Events Grid -->
        <div v-if="otherEvents.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div 
            v-for="event in otherEvents" 
            :key="event._id"
            class="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 group"
          >
            <!-- Event Image -->
            <div class="h-48 overflow-hidden relative">
              <img 
                v-if="event.imageUrl && event.imageUrl.startsWith('data:')" 
                :src="event.imageUrl" 
                :alt="event.title"
                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div v-else class="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <svg class="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                </svg>
              </div>
              <!-- Price Badge -->
              <div class="absolute top-3 right-3">
                <span class="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">Tillgängligt</span>
              </div>
            </div>
            
            <!-- Event Content -->
            <div class="p-6">
              <h3 class="text-xl font-semibold text-gray-900 mb-3 line-clamp-1 group-hover:text-indigo-600 transition-colors">{{ event.title }}</h3>
              <p class="text-gray-600 mb-4 line-clamp-2">{{ event.description }}</p>
              
              <!-- Event Details -->
              <div class="space-y-3 text-sm text-gray-500 mb-6">
                <div class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <span>{{ formatDate(event.date) }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                  <span class="line-clamp-1">{{ event.location }}</span>
                </div>
                <div v-if="event.organizerName" class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                  </svg>
                  <span>{{ event.organizerName }}</span>
                </div>
              </div>
              
              <!-- Action Button -->
              <router-link 
                :to="`/events/${event._id}`"
                class="w-full bg-indigo-600 text-white px-4 py-3 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors text-center block"
              >
                Se detaljer & köp biljett
              </router-link>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-16 bg-white rounded-xl shadow-lg border border-gray-100">
          <div class="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">Inga evenemang tillgängliga</h3>
          <p class="text-gray-600 mb-6">Kolla tillbaka senare för spännande evenemang!</p>
          <div class="flex justify-center space-x-4">
            <button @click="fetchEvents" class="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium">
              Uppdatera
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import LoginRequired from '../components/LoginRequired.vue';

const store = useStore();
const myEvents = ref([]);
const otherEvents = ref([]);
const loading = ref(true);
const error = ref('');

// Get current user from store
const currentUser = computed(() => store.state.user);

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const fetchEvents = async () => {
  try {
    loading.value = true;
    error.value = '';
    
    // Fetch all events
    const response = await axios.get('/api/events');
    const allEvents = response.data;
    console.log('🔍 All events fetched:', allEvents.length);
    console.log('🔍 Sample event:', allEvents[0]);
    
    // Get current user from store instead of making another API call
    const currentUser = store.state.user;
    console.log('🔍 Current user from store:', currentUser);
    console.log('🔍 Current user isOrganizer:', currentUser?.isOrganizer);
    console.log('🔍 Current user role:', currentUser?.role);
    
    if (!currentUser) {
      console.error('❌ No current user found in store');
      error.value = 'Du behöver logga in för att se evenemang';
      return;
    }
    
    // Separate my events from other events
    myEvents.value = allEvents.filter(event => {
      const isMyEvent = String(event.createdBy) === currentUser.userId || String(event.organizerId) === currentUser.userId;
      console.log(`🔍 Event ${event.title}: createdBy=${event.createdBy}, organizerId=${event.organizerId}, myUserId=${currentUser.userId}, isMyEvent=${isMyEvent}`);
      return isMyEvent;
    });
    
    otherEvents.value = allEvents.filter(event => {
      const isNotMyEvent = String(event.createdBy) !== currentUser.userId && String(event.organizerId) !== currentUser.userId;
      return isNotMyEvent;
    });
    
    console.log('🔍 My events count:', myEvents.value.length);
    console.log('🔍 Other events count:', otherEvents.value.length);
    console.log('🔍 My events:', myEvents.value.map(e => ({ title: e.title, createdBy: e.createdBy, organizerId: e.organizerId })));
    
    // Debug the condition for showing "Mina Evenemang" section
    console.log('🔍 Should show Mina Evenemang section:', currentUser && (currentUser.isOrganizer || myEvents.value.length > 0));
    console.log('🔍 Condition breakdown: currentUser exists =', !!currentUser, ', isOrganizer =', currentUser?.isOrganizer, ', myEvents.length > 0 =', myEvents.value.length > 0);
    
  } catch (err) {
    console.error('❌ Error fetching events:', err);
    error.value = 'Kunde inte ladda evenemang. Försök igen senare.';
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchEvents();
});

</script>

<style scoped>
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>


